import Mention from "@tiptap/extension-mention";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";

import { MentionNodeView } from "../nodeViews/MentionNodeView";
import {
  INACTIVE_SUGGESTION,
  MENTION_NODE_NAME,
  MentionSuggestionStore,
  getMentionSuggestionStore,
} from "./mentionSuggestion";

export type MentionMatcher = { name: string; char: string };

export type MentionAttrs = {
  id: string | null;
  label: string | null;
  name: string | null;
  href: string | null;
};

/*
Mentions are stored as anchors carrying `data-mention-atom-id` / `data-mention-atom-name`, which the
backend parses to resolve notifications. The two data attributes must stay adjacent in the rendered
markup because the Python parser matches them as a pair.
*/
export const MentionAtom = Mention.extend({
  name: MENTION_NODE_NAME,

  priority: 1100,

  selectable: true,

  addStorage() {
    return { suggestion: new MentionSuggestionStore() };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-atom-id"),
        renderHTML: () => ({}),
      },
      name: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-atom-name"),
        renderHTML: () => ({}),
      },
      label: {
        default: null,
        parseHTML: (element) => element.textContent,
        renderHTML: () => ({}),
      },
      href: {
        default: null,
        parseHTML: (element) => element.getAttribute("href"),
        renderHTML: () => ({}),
      },
      mentionSuggestionChar: { default: "@", renderHTML: () => ({}) },
    };
  },

  parseHTML() {
    return [{ tag: "a[data-mention-atom-id]", priority: 60 }];
  },

  renderHTML({ node }) {
    const { id, label, name, href } = node.attrs as MentionAttrs;
    const attributes: Record<string, string> = {
      class: name ? `mention mention-${name}` : "mention",
      "data-mention-atom-id": id ?? "",
      "data-mention-atom-name": name ?? "",
      contenteditable: "false",
    };
    if (href) {
      attributes.href = href;
    }
    return ["a", attributes, label ?? id ?? ""];
  },

  renderText({ node }) {
    const { label, id } = node.attrs as MentionAttrs;
    return label ?? id ?? "";
  },
});

type SuggestionConfig = Omit<SuggestionOptions, "editor">;

const createSuggestion = (
  char: string,
  options: { allowSpaces: boolean; asPlainText: boolean },
): SuggestionConfig => ({
  // Each suggestion gets its own plugin key from the mention extension, so one per trigger char is
  // registered independently.
  char,
  allowSpaces: options.allowSpaces,
  allowedPrefixes: null,
  decorationClass: "mention-suggest",
  items: () => [],
  ...(options.asPlainText
    ? {
        command: ({ editor, range, props }) => {
          const item = props as Record<string, unknown>;
          const text = String(item.emoji ?? item.label ?? "");
          editor.chain().focus().insertContentAt(range, `${text} `).run();
        },
      }
    : {}),
  render: () => {
    let store: MentionSuggestionStore | null = null;

    const publish = (props: SuggestionProps, active: boolean) => {
      store = getMentionSuggestionStore(props.editor);
      if (!store) {
        return;
      }

      if (!active) {
        store.setState(INACTIVE_SUGGESTION);
        return;
      }

      store.setState({
        active: true,
        char,
        query: props.query ?? "",
        clientRect: props.clientRect ?? null,
        command: props.command,
      });
    };

    return {
      onStart: (props) => publish(props, true),
      onUpdate: (props) => publish(props, true),
      onExit: (props) => publish(props, false),
      onKeyDown: ({ event }) => store?.handleKeyDown(event) ?? false,
    };
  },
});

export const createMentionExtension = (
  configs: Array<{ name?: string; char?: string }>,
  options: { emojiChar?: string; withNodeView?: boolean } = {},
) => {
  const chars: string[] = [];
  for (const config of configs) {
    if (config.char && config.name && !chars.includes(config.char)) {
      chars.push(config.char);
    }
  }

  const suggestions = chars.map((char) =>
    createSuggestion(char, { allowSpaces: true, asPlainText: false }),
  );

  if (options.emojiChar && !chars.includes(options.emojiChar)) {
    suggestions.push(
      createSuggestion(options.emojiChar, {
        allowSpaces: false,
        asPlainText: true,
      }),
    );
  }

  const extension = options.withNodeView
    ? MentionAtom.extend({
        addNodeView() {
          return ReactNodeViewRenderer(MentionNodeView);
        },
      })
    : MentionAtom;

  return extension.configure({ suggestions });
};
